import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { cx } from '@parrot/utils';
import styles from './Input.module.css';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  /** The native `size` attribute (a character-width hint) is omitted above, so this reuses the name. */
  size?: 'sm' | 'md' | 'lg';
  /** Rendered above the field and wired up with `htmlFor`. Omit only if something else labels it. */
  label?: ReactNode;
  /** Icon inside the field, leading edge. Pass an element: `iconLeft={<Search size={16} />}`. */
  iconLeft?: ReactNode;
  /** Icon inside the field, trailing edge. Superseded by the clear button when one is showing. */
  iconRight?: ReactNode;
  /** Provide to get a clear button whenever the field has a value. */
  onClear?: () => void;
  /** Tints the border and the message. */
  status?: 'error' | 'success' | 'warning';
  /** Helper text below the field. Turns into the error/success/warning message when `status` is set. */
  statusMessage?: ReactNode;
  ref?: Ref<HTMLInputElement>;
};

/**
 * A single-line text field. Renders a real `<input>`, so every native attribute
 * passes through.
 *
 * Owns its `<label>` rather than leaving it to callers — generating the `id`/
 * `htmlFor` pair here is what makes the association reliable.
 *
 * Icons are `ReactNode` slots, never imported: `libs/ui` carries no icon dependency.
 */
export const Input = ({
  size = 'md',
  label,
  iconLeft,
  iconRight,
  onClear,
  status,
  statusMessage,
  className,
  id,
  value,
  ref,
  ...inputProps
}: Props) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;

  const showClear = onClear !== undefined && value !== undefined && value !== '';
  const trailing = showClear ? undefined : iconRight;

  return (
    <div
      className={cx(
        styles.base,
        styles[size],
        status !== undefined && styles[status],
        iconLeft !== undefined && styles.hasIconLeft,
        (trailing !== undefined || showClear) && styles.hasIconRight,
        className,
      )}
    >
      {label !== undefined && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      )}

      <div className={styles.field}>
        {iconLeft !== undefined && (
          <span className={styles.iconLeft} aria-hidden={'true'}>
            {iconLeft}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          className={styles.input}
          value={value}
          aria-invalid={status === 'error' || undefined}
          aria-describedby={statusMessage !== undefined ? messageId : undefined}
          {...inputProps}
        />

        {showClear && (
          <button
            type={'button'}
            className={styles.clear}
            onClick={onClear}
            aria-label={'Clear input'}
          >
            <svg
              width={14}
              height={14}
              viewBox={'0 0 24 24'}
              fill={'none'}
              stroke={'currentColor'}
              strokeWidth={2}
              strokeLinecap={'round'}
              aria-hidden={'true'}
            >
              <path d={'M18 6 6 18M6 6l12 12'} />
            </svg>
          </button>
        )}

        {trailing !== undefined && (
          <span className={styles.iconRight} aria-hidden={'true'}>
            {trailing}
          </span>
        )}
      </div>

      {statusMessage !== undefined && (
        <p id={messageId} className={styles.message}>
          {statusMessage}
        </p>
      )}
    </div>
  );
};
